import React from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { getDocumentRightsByStateKey } from 'constants/common';
import get from 'lodash.get';
import flow from 'lodash.flow';
import curry from 'lodash.curry';
import * as editableDocument from 'utils/model/editableDocument';
import { defaultAddressTypes } from 'constants/defaultAddressTypes';
import {
  getAddressTypes,
  getAddressTypesLoaded,
  getClientData,
  getCountiesLoadedByCountryId,
  getCountiesLoadingByCountryId,
  getDefaultAddressData,
  getExternalVerificationStatusByUniqueId,
  getFullPartner,
  getOfferType,
  getPartnerByUniqueId,
  getPartnerAddresses,
  getShouldRefreshAddresses,
  getPartnerExpanded,
  getPartnerFromQuery,
  getPartnerIsLoaded,
  getPartnerIsLoadedByUniqueId,
  getPartnerLoading,
  getRefreshedOfferPartnerStatus,
  getRealBeneficiaryRole
} from '../selectors';
import { getPartnerFileData } from 'features/partnerFile/selectors';
import {
  cancelOfferClientChanges,
  finishPartnerQuery,
  getNationalityByUniqueId,
  loadCounties,
  loadDefaultPartnerAddress,
  loadDocumentPartner,
  loadPartnerAdresses,
  loadPartnerByUniqueId,
  onPrintGDPR,
  saveOfferPartner,
  setBirthDateFromCNP,
  setDefaultPartnerAddress,
  togglePartnerFormCard,
  refreshAddressesFinished
} from '../actionCreators';
import { createPartnerFile } from 'features/partnerFile/actionCreators';
import { loadAddressTypes } from 'actions/actionCreators/dictionariesActions';
import {
  checkBirthDateAccordingToUniqueId,
  dateIsGreaterThan,
  dateIsLessThan,
  isBirthDateAccordingToUniqueId,
  validateCompanyOrPersonUniqueId,
  isDerivedInRange,
  isEmail,
  isInInterval,
  isMandatory,
  hasLengthLessThan,
  isSeriesAndNumber,
  multipleEmailsValid,
  noSpaceAtBeginning,
  phone,
  isValidDateFormat,
  foreach,
  validatePersonUniqueId,
  validateCompanyUniqueId,
  extractBirthDateFromUniqueId
} from 'utils/simple-validations';
import { both, valueChanged, when } from 'utils/fn-combinators';
import { isCIF as validateCIF, isCNP } from '@totalsoft/validations';
import { extractAgeFromCnp } from 'utils/functions';
import { maximumAge, minimumAge, addressFieldMaxLength } from 'constants/numericLimits';
import OfferPartnerFormCard from './OfferPartnerFormCard';
import { withParams } from 'providers/ParamsConsumer';
import { getFilteredPartnerTypes } from '../../../assets/client/selectors';
import { CREATE_PARTNER_FILE_REDIRECTED } from 'features/partnerFile/actionTypes';

const getValidationRulesForPJ = (checkMandatoryFields, applyUniqueIdRules, countryCode, multipleEmailAddresses, isAssociatedPartner) => {
  return {
    generalData: {
      uniqueId: [when(checkMandatoryFields, isMandatory()), when(applyUniqueIdRules, validateCompanyUniqueId(countryCode))],
      partnerName: [when(checkMandatoryFields, isMandatory()), hasLengthLessThan(200)],
      registrationDate: [when(checkMandatoryFields, isMandatory()), isValidDateFormat()],
      legalFormId: [when(checkMandatoryFields, isMandatory())],
      comercialRegistration: [when(checkMandatoryFields, isMandatory())]
    },
    contactMode: {
      phone: [phone(countryCode)],
      email: [multipleEmailAddresses ? multipleEmailsValid(countryCode) : isEmail()]
    },
    partnerRoleId: [when(isAssociatedPartner, isMandatory())],
    addresses: foreach(_ => ({
      country: [when(checkMandatoryFields, isMandatory())],
      county: [when(checkMandatoryFields, isMandatory())],
      city: [when(checkMandatoryFields, isMandatory())],
      street: [when(checkMandatoryFields, isMandatory()), hasLengthLessThan(addressFieldMaxLength['street'])],
      number: [hasLengthLessThan(addressFieldMaxLength['number'])],
      building: [hasLengthLessThan(addressFieldMaxLength['building'])],
      stair: [hasLengthLessThan(addressFieldMaxLength['stair'])],
      flatNumber: [hasLengthLessThan(addressFieldMaxLength['flatNumber'])],
      floor: [hasLengthLessThan(addressFieldMaxLength['floor'])],
      zipCode: [hasLengthLessThan(addressFieldMaxLength['zipCode'])],
      neighborhood: [hasLengthLessThan(addressFieldMaxLength['neighborhood'])]
    }))
  };
};

const getValidationRulesForPFA = (checkMandatoryFields, applyUniqueIdRules, countryCode, multipleEmailAddresses, isAssociatedPartner) => {
  return {
    generalData: {
      uniqueId: [when(checkMandatoryFields, isMandatory()), when(applyUniqueIdRules, validateCompanyOrPersonUniqueId(countryCode))],
      partnerName: [when(checkMandatoryFields, isMandatory()), hasLengthLessThan(200)],
      registrationDate: [when(checkMandatoryFields, isMandatory()), isValidDateFormat()],
      legalFormId: [when(checkMandatoryFields, isMandatory())]
    },
    contactMode: {
      phone: [phone(countryCode)],
      email: [multipleEmailAddresses ? multipleEmailsValid() : isEmail()]
    },
    partnerRoleId: [when(isAssociatedPartner, isMandatory())],
    addresses: foreach(_ => ({
      country: [when(checkMandatoryFields, isMandatory())],
      county: [when(checkMandatoryFields, isMandatory())],
      city: [when(checkMandatoryFields, isMandatory())],
      street: [when(checkMandatoryFields, isMandatory()), hasLengthLessThan(addressFieldMaxLength['street'])],
      number: [hasLengthLessThan(addressFieldMaxLength['number'])],
      building: [hasLengthLessThan(addressFieldMaxLength['building'])],
      stair: [hasLengthLessThan(addressFieldMaxLength['stair'])],
      flatNumber: [hasLengthLessThan(addressFieldMaxLength['flatNumber'])],
      floor: [hasLengthLessThan(addressFieldMaxLength['floor'])],
      zipCode: [hasLengthLessThan(addressFieldMaxLength['zipCode'])],
      neighborhood: [hasLengthLessThan(addressFieldMaxLength['neighborhood'])]
    }))
  };
};

const getValidationRulesForPF = (
  checkMandatoryFields,
  applyUniqueIdRules,
  minAge,
  maxAge,
  uniqueIdErrorMessage,
  stateUniqueId,
  isPassport,
  identityCardExpireDate,
  identityCardReleaseDate,
  countryCode,
  multipleEmailAddresses,
  isAssociatedPartner,
  validityDaysForIdentityCard,
  isExternalPartner,
  applyBirthDateRule,
  offerPartnerExtendedInformationRead
) => {
  return {
    generalData: {
      uniqueId: [
        when(checkMandatoryFields, isMandatory()),
        when(applyUniqueIdRules, validatePersonUniqueId(countryCode), {
          ...isDerivedInRange(extractAgeFromCnp, minAge, maxAge),
          msg: uniqueIdErrorMessage
        })
      ],
      firstName: [when(checkMandatoryFields, isMandatory()), hasLengthLessThan(150)],
      lastName: [when(checkMandatoryFields, isMandatory()), hasLengthLessThan(150)],
      personDocumentTypeId: [when(checkMandatoryFields, isMandatory())],
      birthDate: [
        when(checkMandatoryFields, isMandatory()),
        isValidDateFormat(),
        when(
          applyBirthDateRule && !isExternalPartner && offerPartnerExtendedInformationRead,
          isBirthDateAccordingToUniqueId(stateUniqueId, countryCode)
        )
      ],
      idSeriesAndNumber: [
        when(checkMandatoryFields, isMandatory()),
        when(!isPassport && !isExternalPartner, isSeriesAndNumber(countryCode)),
        noSpaceAtBeginning()
      ],
      idIssuedBy: [when(checkMandatoryFields, isMandatory())],

      identityCardReleaseDate: [
        when(checkMandatoryFields, isMandatory()),
        isValidDateFormat(),
        when(Boolean(identityCardExpireDate) && Boolean(identityCardReleaseDate), dateIsLessThan(identityCardExpireDate))
      ],
      identityCardExpireDate: [
        when(checkMandatoryFields, isMandatory()),
        isValidDateFormat(),
        when(Boolean(identityCardExpireDate) && Boolean(identityCardReleaseDate), dateIsGreaterThan(identityCardReleaseDate)),
        when(Boolean(identityCardExpireDate) && Boolean(identityCardReleaseDate), isInInterval(validityDaysForIdentityCard))
      ],

      citizenshipId: [when(checkMandatoryFields, isMandatory())],
      nationalityId: [when(checkMandatoryFields, isMandatory())],
      genderId: [when(checkMandatoryFields, isMandatory())]
    },
    contactMode: {
      phone: [phone(countryCode)],
      email: [multipleEmailAddresses ? multipleEmailsValid() : isEmail()]
    },
    partnerRoleId: [when(isAssociatedPartner, isMandatory())],
    addresses: foreach(_ => ({
      country: [when(checkMandatoryFields, isMandatory())],
      county: [when(checkMandatoryFields, isMandatory())],
      city: [when(checkMandatoryFields, isMandatory())],
      street: [when(checkMandatoryFields, isMandatory()), hasLengthLessThan(addressFieldMaxLength['street'])],
      number: [hasLengthLessThan(addressFieldMaxLength['number'])],
      building: [hasLengthLessThan(addressFieldMaxLength['building'])],
      stair: [hasLengthLessThan(addressFieldMaxLength['stair'])],
      flatNumber: [hasLengthLessThan(addressFieldMaxLength['flatNumber'])],
      floor: [hasLengthLessThan(addressFieldMaxLength['floor'])],
      zipCode: [hasLengthLessThan(addressFieldMaxLength['zipCode'])],
      neighborhood: [hasLengthLessThan(addressFieldMaxLength['neighborhood'])]
    }))
  };
};

const getValidationRules = (
  partnerTypeId,
  partnerTypes,
  minAge,
  maxAge,
  uniqueIdErrorMessage,
  identityCardExpireDate,
  identityCardReleaseDate,
  isAssociatedPartner,
  checkMandatoryFields,
  applyUniqueIdRules,
  validityDaysForIdentityCard,
  stateUniqueId,
  multipleEmailAddresses,
  countryCode,
  isPassport,
  isExternalPartner,
  applyBirthDateRule,
  offerPartnerExtendedInformationRead
) => {
  const partnerType = partnerTypes.find(pt => pt.partnerTypeId === partnerTypeId);

  if (!partnerTypeId || !partnerType || (partnerType && partnerType.typeCheck.isPF)) {
    return getValidationRulesForPF(
      checkMandatoryFields,
      applyUniqueIdRules,
      minAge,
      maxAge,
      uniqueIdErrorMessage,
      stateUniqueId,
      isPassport,
      identityCardExpireDate,
      identityCardReleaseDate,
      countryCode,
      multipleEmailAddresses,
      isAssociatedPartner,
      validityDaysForIdentityCard,
      isExternalPartner,
      applyBirthDateRule,
      offerPartnerExtendedInformationRead
    );
  }

  if (partnerType.typeCheck.isPJ) {
    return getValidationRulesForPJ(checkMandatoryFields, applyUniqueIdRules, countryCode, multipleEmailAddresses, isAssociatedPartner);
  }

  if (partnerType.typeCheck.isPFA) {
    return getValidationRulesForPFA(checkMandatoryFields, applyUniqueIdRules, countryCode, multipleEmailAddresses, isAssociatedPartner);
  }
};

const getBusinessRules = (genderTypes, defaultCountryId) => {
  return {
    generalData: {
      isExternal: when(
        valueChanged(partner => get(partner, '0.country')),
        document =>
          document?.addresses?.find(x => x.isDefault === true)?.country.id &&
          document?.addresses?.find(x => x.isDefault === true)?.country.id !== defaultCountryId
      ),
      genderId: when(
        both(
          valueChanged(partner => get(partner, 'generalData.isExternal')),
          partner => !get(partner, 'generalData.isExternal')
        ),
        document => getGenderByUniqueId(genderTypes, get(document, 'generalData.uniqueId'))
      )
    }
  };
};

const getGenderByUniqueId = (genderTypes, uniqueId) => {
  if (!uniqueId) return null;
  if (!isCNP(uniqueId)) return null;

  const maleDigits = ['1', '3', '5', '7'];
  const femaleDigits = ['2', '4', '6', '8'];
  const firstUniqueIdDigit = uniqueId.charAt(0);

  if (maleDigits.includes(firstUniqueIdDigit)) return genderTypes.find(gen => gen.code === 'M').id;
  if (femaleDigits.includes(firstUniqueIdDigit)) return genderTypes.find(gen => gen.code === 'F').id;
};

class OfferPartnerFormContainer extends React.PureComponent {
  state = {
    loadingByUniqueId: false,
    partner: null,
    partnerFromModal: false,
    isSaving: false,
    canSearchExternally: {
      uniqueIdIsValid: true,
      uniqueIdIsCIF: null,
      partnerNameIsValid: true
    },
    prevProps: {
      partner: null,
      partnerByUniqueId: null,
      partnerFromQuery: null
    }
  };

  static getDerivedStateFromProps(props, state) {
    const prevProps = state.prevProps;

    if (
      !!props.isExternalVerificationInProgress &&
      !!props.isExternalVerificationInProgress !== !!prevProps.isExternalVerificationInProgress
    ) {
      return {
        prevProps: {
          partner: {
            ...props.partner,
            dirtyInfo: {
              ...props.partner.dirtyInfo,
              isDirty: false
            }
          },
          isExternalVerificationInProgress: props.isExternalVerificationInProgress
        }
      };
    }

    if (
      (!props.isExternalVerificationInProgress &&
        !!props.isExternalVerificationInProgress !== !!prevProps.isExternalVerificationInProgress) ||
      props.partnerIsFromPartnerQuery
    ) {
      const partner = editableDocument.create(props.partner);
      props.actions.finishPartnerQuery();

      return {
        prevProps: {
          partner: props.partner
        },
        partner
      };
    }

    if (props.isLoadedByUniqueId && props.partnerByUniqueId !== prevProps.partnerByUniqueId) {
      let partner = editableDocument.create(props.partnerByUniqueId);

      if (state.partner?.document) {
        if (state.partner.document.partnerType?.typeCheck?.isPF && state.partner.document.agreesWithPersonalDataUse) {
          partner = editableDocument.update('agreesWithPersonalDataUse', true, null, partner);
        } else {
          partner = editableDocument.update('agreesWithPersonalDataUse', false, null, partner);
        }

        if (state.partner.document.partnerRoleId !== prevProps?.partner?.partnerRoleId) {
          partner = flow(
            editableDocument.update('partnerRoleId', state.partner.document.partnerRoleId, null),
            editableDocument.update('partnerRoleCode', state.partner.document.partnerRoleCode, null),
            editableDocument.update('partnerRoleName', state.partner.document.partnerRoleName, null)
          )(partner);
        }
      }
      partner = editableDocument.update('partnerType', state.partner.document.partnerType, null, partner);

      return {
        prevProps: {
          partner: props.partner,
          partnerByUniqueId: props.partnerByUniqueId
        },
        partner
      };
    }

    if (props.isRealBeneficiary && state.partner && state.partner.document && state.partner.document.partnerRoleId === null) {
      let partner = editableDocument.create(props.partner);
      partner = flow(
        editableDocument.update('partnerRoleId', props.offerConfig.realBeneficiaryPartnerRoleId, null),
        editableDocument.update('partnerRoleCode', props.offerConfig.realBeneficiaryPartnerRoleCode, null),
        editableDocument.update('partnerRoleName', props.offerConfig.realBeneficiaryPartnerRoleName, null)
      )(partner);

      return {
        prevProps: {
          partner: props.partner
        },
        partner
      };
    }

    if (props.partner !== prevProps.partner) {
      let partner = editableDocument.create(props.partner);

      if (state.partner?.document?.agreesWithPersonalDataUse !== prevProps.partner?.agreesWithPersonalDataUse)
        partner = editableDocument.update('agreesWithPersonalDataUse', true, null, partner);

      if (state.partner?.document && state.partner.dirtyInfo.isDirty) {
        partner = flow(
          editableDocument.update('partnerRoleId', state.partner.document.partnerRoleId, null),
          editableDocument.update('partnerRoleCode', state.partner.document.partnerRoleCode, null),
          editableDocument.update('partnerRoleName', state.partner.document.partnerRoleName, null)
        )(partner);

        if (
          state.partner.document.contactMode &&
          Object.keys(state.partner.document.contactMode).length !== 0 &&
          JSON.stringify(partner.document.contactMode) !== JSON.stringify(state.partner.document.contactMode)
        ) {
          partner = editableDocument.update('contactMode', state.partner.document.contactMode, null, partner);
        }

        if (
          state.partner.document.addresses.length !== 0 &&
          JSON.stringify(partner.document.addresses) !== JSON.stringify(state.partner.document.addresses)
        ) {
          partner = editableDocument.update('addresses', state.partner.document.addresses, null, partner);
        }

        if (
          Object.keys(state.partner.document.generalData).length !== 0 &&
          JSON.stringify(partner.document.generalData) !== JSON.stringify(state.partner.document.generalData)
        ) {
          partner = editableDocument.update('generalData', state.partner.document.generalData, null, partner);
        }

        if (
          state.partner.document.partnerType &&
          Object.keys(state.partner.document.partnerType).length !== 0 &&
          JSON.stringify(partner.document.partnerType) !== JSON.stringify(state.partner.document.partnerType)
        ) {
          partner = editableDocument.update('partnerType', state.partner.document.partnerType, null, partner);
        }
      }

      return {
        prevProps: {
          partner: props.partner,
          partnerByUniqueId: props.partnerByUniqueId
        },
        partner
      };
    }

    return null;
  }

  componentDidMount() {
    this.loadOfferPartnerData();
    this.loadPartnerAddresses();
    this.loadAddressTypes();
  }

  componentDidUpdate(prevProps) {
    if (this.props.dictionaryAddressTypeLoaded !== prevProps.dictionaryAddressTypeLoaded && !this.props.dictionaryAddressTypeLoaded)
      this.loadAddressTypes();

    if (this.props.isLoaded !== prevProps.isLoaded && !this.props.isLoaded) {
      this.loadOfferPartnerData();
    }

    if (this.props.isLoaded && this.props.partner?.generalData?.uniqueId && this.state.canSearchExternally?.uniqueIdIsCIF == null) {
      this.setState(prevState => ({
        ...prevState,
        canSearchExternally: {
          ...prevState.canSearchExternally,
          uniqueIdIsCIF: validateCIF(this.props.partner.generalData.uniqueId)
        }
      }));
    }

    if (this.props.partnerAddressesLoaded !== prevProps.partnerAddressesLoaded) {
      this.loadPartnerAddresses();
    }

    if (this.props.shouldRefreshAddresses) {
      this.handleAddressesOnRefresh();
    }

    if (
      this.props.isLoaded &&
      this.props.partner.partnerType &&
      (!this.props.defaultAddress[this.props.partner.partnerType.partnerTypeId] ||
        (!this.props.defaultAddress[this.props.partner.partnerType.partnerTypeId].isLoaded &&
          !this.props.defaultAddress[this.props.partner.partnerType.partnerTypeId].isLoading))
    )
      this.loadDefaultPartnerAddress();

    if (this.props.isLoadedByUniqueId) {
      const defaultAddress = (this.props.partnerByUniqueId?.addresses || []).find(addr => addr.isDefault);
      if ((!this.props.partnerByUniqueId?.addresses?.length || !defaultAddress) && this.state.partner.document.partnerType) {
        this.setDefaultAddress(
          null,
          this.props.partnerByUniqueId.generalData.uniqueId,
          this.props.defaultAddress[this.state.partner.document.partnerType.partnerTypeId]
        );
      }

      if (
        this.props.partnerByUniqueId.generalData.uniqueId &&
        this.props.partnerByUniqueId.generalData.uniqueId.length === 13 &&
        this.props.partnerByUniqueId.generalData.birthDate &&
        (this.props.partnerByUniqueId.generalData.birthDate !== prevProps.partnerByUniqueId.generalData.birthDate ||
          !prevProps.partnerByUniqueId.generalData.birthDate)
      )
        this.setBirthDate(this.props.partnerByUniqueId.generalData.uniqueId, this.props.partnerByUniqueId.generalData.birthDate);
    }

    if (this.props.isLoaded) {
      if (
        this.props.partner.partnerType &&
        this.props.defaultAddress[this.props.partner.partnerType.partnerTypeId] &&
        this.props.defaultAddress[this.props.partner.partnerType.partnerTypeId].isLoaded
      ) {
        const defaultAddress = this.props.partner?.addresses?.find(addr => addr?.isDefault);
        if (!this.props.partner.addresses?.length || !defaultAddress) {
          this.setDefaultAddress(
            this.props.partner.partnerId,
            null,
            this.props.defaultAddress[this.props.partner.partnerType.partnerTypeId]
          );
        }
      }
    }

    if (this.props.isLoaded !== prevProps.isLoaded && this.props.isLoaded) {
      if (this.props.partner.addresses && this.props.partner.addresses.length > 0) {
        let countryId;
        const defaultAddress = this.props.partner.addresses.find(a => a?.isDefault);
        this.loadPartnerAddresses();

        if (!defaultAddress) countryId = this.props.offerConfig.defaultCountry.id;
        else countryId = defaultAddress.country.id;

        if (!countryId) {
          //offerConfig is NOT loaded
          countryId = this.props.offerConfig.defaultCountry.id;
        }
        if (!this.props.countiesLoaded) {
          if (!this.props.countiesLoading) this.props.actions.loadCounties('', countryId);
        }
      }
    }

    const { saved, failed, documentId, siteId } = this.props.partnerFileData;
    if (saved) {
      this.props.dispatch({ type: CREATE_PARTNER_FILE_REDIRECTED });
      this.props.history.push(`/partner-files/${documentId}/${siteId}`);
    }

    if (failed)
      this.setState(prevState => ({
        ...prevState,
        partner: {
          ...prevState.partner,
          dirtyInfo: {
            ...prevState.partner.dirtyInfo,
            isDirty: true
          }
        }
      }));

    if (prevProps.saving && !this.props.saving) {
      this.setState(prevState => ({
        ...prevState,
        isSaving: false
      }));
    }
  }

  setIsPartnerFromModal = value => {
    this.setState({
      partnerFromModal: value
    });
  };

  checkMandatoryFields = () => {
    const partnerDataValidationMomentValue = this.props.params.partnerDataValidationMoment;
    if (!partnerDataValidationMomentValue) return true;
    return partnerDataValidationMomentValue.toUpperCase().trim() === 'SAVE';
  };

  onAddAddress = (partner, addressType) => {
    let partnerAddresses = partner.addresses.map(address => Object.assign({}, address));
    const defaultAddress = defaultAddressTypes.find(item => item === addressType.code);
    if (defaultAddress) {
      const socialAddress = partnerAddresses
        ?.filter(x => x.addressTypeCode === addressType.code)
        ?.map(x => ({ ...x, isActive: false, isDefault: false }));
      partnerAddresses = partnerAddresses?.filter(x => x.addressTypeCode !== addressType.code)?.concat(socialAddress);
    }
    partnerAddresses.push({
      country: { id: this.props.offerConfig.defaultCountry.id, name: this.props.offerConfig.defaultCountry.name },
      addressTypeCode: addressType.code,
      addressTypeName: addressType.name,
      isDefault: defaultAddress ? true : addressType.isDefault,
      isActive: true
    });

    this.setState(prevState => ({
      ...prevState,
      partner: editableDocument.update('addresses', partnerAddresses, null, prevState.partner)
    }));
  };

  onDeleteAddress = index => {
    const partnerAddresses = this.state.partner.document.addresses.map(address => Object.assign({}, address));
    const newPartnerAddresses = partnerAddresses.filter(a => partnerAddresses.indexOf(a) !== index);

    this.setState(prevState => ({
      partner: flow(
        editableDocument.update('addresses', newPartnerAddresses, null),
        editableDocument.resetValidationInfo
      )(prevState.partner)
    }));
  };

  componentWillUnmount() {
    const { documentId, siteId, partnerId, isAssociatedPartner, isRealBeneficiary, actions } = this.props;
    actions.cancelOfferClientChanges(documentId, siteId, partnerId, isAssociatedPartner, isRealBeneficiary);
  }

  applyUniqueIdRules = (uniqueId, isExternal) => {
    if (isExternal) return false;
    if (uniqueId?.toUpperCase()?.startsWith(this.props.params.keysFinNextIdPrefix)) return false;
    const specialIds = this.props.params.specialPartnerUniqueIds;
    if (uniqueId !== null && specialIds?.includes(uniqueId)) return false;

    return true;
  };

  applyBirthDateRule = uniqueId => {
    if (Boolean(uniqueId) === false) return false;
    if (uniqueId?.toUpperCase()?.startsWith(this.props.params.keysFinNextIdPrefix)) return false;
    return true;
  };

  getBusinessRules = () => {
    return getBusinessRules(this.props.genderTypes, this.props.offerConfig.defaultCountry.id);
  };

  loadOfferPartnerData = () => {
    const { documentId, siteId, partnerId, documentTypeId, actions } = this.props;
    actions.loadDocumentPartner(documentId, siteId, partnerId, documentTypeId);
  };

  loadPartnerAddresses = () => {
    const { documentId, siteId, partnerId, actions } = this.props;
    if (partnerId) actions.loadPartnerAdresses(documentId, siteId, partnerId);
  };

  handleAddressesOnRefresh = () => {
    if (this.props.partnerAddresses.length === 0) {
      this.setDefaultAddress(
        this.props.partner.partnerId,
        this.props.partnerByUniqueId.generalData.uniqueId,
        this.props.defaultAddress[this.state.partner.document.partnerType.partnerTypeId]
      );
    } else {
      this.setState(prevState => ({
        ...prevState,
        partner: {
          ...prevState.partner,
          document: {
            ...prevState.partner.document,
            addresses: this.props.partnerAddresses.filter(addresses => addresses.isActive)
          }
        }
      }));
    }
    this.props.actions.refreshAddressesFinished(this.props.partnerId);
  };

  loadDefaultPartnerAddress = () => {
    const { partnerType } = this.props.partner;
    this.props.actions.loadDefaultPartnerAddress(partnerType?.partnerTypeId);
  };

  setDefaultAddress = (partnerId, uniqueId, defaultAddress) =>
    this.props.actions.setDefaultPartnerAddress(partnerId, uniqueId, defaultAddress);

  setBirthDate = (uniqueId, actualBirthDate) => {
    if (!checkBirthDateAccordingToUniqueId(uniqueId, actualBirthDate, this.props.params.countryCode))
      this.props.actions.setBirthDateFromCNP(uniqueId, extractBirthDateFromUniqueId(uniqueId, this.props.params.countryCode));
  };

  loadAddressTypes = () => {
    const { actions } = this.props;
    actions.loadAddressTypes();
  };

  onGDPRPrint = async () => {
    const { documentId, siteId, isAssociatedPartner, isRealBeneficiary, partnerId } = this.props;
    this.props.actions.onPrintGDPR(documentId, siteId, partnerId, isAssociatedPartner, isRealBeneficiary);
  };

  handleSave = () => {
    const { generalData, contactMode, partnerType } = this.state.partner.document;
    const { isAssociatedPartner, partnerTypes, idTypes } = this.props;
    const selectedIdTypeCode = idTypes
      ? idTypes.find(x => x.id === this.state.partner.document.generalData.personDocumentTypeId)?.code
      : '';
    const minAge = generalData.minimumAge || minimumAge;
    const maxAge = generalData.maximumAge || maximumAge;
    const uniqueId = generalData.uniqueId?.trim();
    const uniqueIdErrorMessage = this.props.t('Validations.Client.Age', {
      data: { minimumAge: minAge, maximumAge: maxAge }
    });
    const { validityDaysForIdentityCard } = this.props.params;
    const hasMultipleEmailAddresses = Boolean(contactMode?.email?.split(';').length > 1);

    this.setState(prevState => ({
      ...prevState,
      partner: {
        ...prevState.partner,
        document: {
          ...prevState.partner.document,
          generalData: {
            ...prevState.partner.document.generalData,
            uniqueId
          }
        }
      },
      canSearchExternally: {
        uniqueIdIsValid: this.checkMandatoryFields() ? !!uniqueId : true,
        uniqueIdIsCIF: validateCIF(uniqueId),
        partnerNameIsValid: this.checkMandatoryFields() ? !!generalData.partnerName : true
      }
    }));

    this.setState(
      currentState => {
        const partner = editableDocument.validate(
          getValidationRules(
            partnerType?.partnerTypeId,
            partnerTypes,
            minAge,
            maxAge,
            uniqueIdErrorMessage,
            generalData.identityCardExpireDate,
            generalData.identityCardReleaseDate,
            !!isAssociatedPartner,
            this.checkMandatoryFields(),
            this.applyUniqueIdRules(uniqueId, currentState.partner?.document?.generalData?.isExternal),
            validityDaysForIdentityCard,
            uniqueId,
            hasMultipleEmailAddresses,
            this.props.params.countryCode,
            selectedIdTypeCode === 'PAS',
            currentState.partner?.document?.generalData?.isExternal,
            this.applyBirthDateRule(uniqueId),
            this.props.offerPartnerExtendedInformationRead
          ),
          false,
          currentState.partner
        );
        if (!partner.validationInfo.isValid) {
          return { partner };
        }
      },
      () => {
        const { documentId, siteId, partnerId, isAssociatedPartner } = this.props;
        const isPartnerValid = this.state.partner.validationInfo.isValid;

        if (isPartnerValid) {
          if (window.location.pathname.includes('partner-files/new')) {
            this.props.actions.createPartnerFile(this.state.partner.document);
            this.setState(prevState => ({
              ...prevState,
              partner: {
                ...prevState.partner,
                dirtyInfo: {
                  ...prevState.partner.dirtyInfo,
                  isDirty: false
                }
              }
            }));
          } else {
            this.setState({ isSaving: true });

            this.props.actions.saveOfferPartner(documentId, siteId, partnerId, this.state.partner.document, isAssociatedPartner);
          }
        }
      }
    );
  };

  handleExternalSearch = (callbackFunction = () => {}) => {
    const {
      generalData: { uniqueId, partnerName }
    } = this.state.partner.document;
    this.setState(
      prevState => ({
        ...prevState,
        canSearchExternally: {
          uniqueIdIsValid: !!uniqueId,
          uniqueIdIsCIF: validateCIF(uniqueId),
          partnerNameIsValid: !!partnerName
        }
      }),
      () => callbackFunction()
    );
  };

  handleUniqueIdBlur = uniqueId => {
    const { partnerTypes } = this.props;
    const { partnerType } = this.state.partner.document;
    if (
      uniqueId &&
      this.state.partner.validationInfo.generalData &&
      !this.state.partner.validationInfo.generalData.uniqueId &&
      (!this.state.prevProps.partnerByUniqueId || this.state.prevProps.partnerByUniqueId.generalData.uniqueId?.trim() !== uniqueId.trim())
    ) {
      const { partnerId } = this.props;
      const partnerTypeId = partnerType?.partnerTypeId;

      if (uniqueId && !!partnerTypeId) {
        this.setState(prevState => ({
          ...prevState,
          loadingByUniqueId: true
        }));
        try {
          const partnerType = partnerTypes.find(pt => pt.partnerTypeId === partnerTypeId);
          this.props.actions
            .loadPartnerByUniqueId(this.props.documentId, this.props.siteId, uniqueId, partnerId, partnerType, (uniqueId, partnerName) => {
              this.setState(prevState => ({
                ...prevState,
                canSearchExternally: {
                  ...prevState.canSearchExternally,
                  partnerNameIsValid: this.checkMandatoryFields() ? !!partnerName : true,
                  uniqueIdIsValid: this.checkMandatoryFields() ? !!uniqueId : true
                }
              }));
            })
            .then(() => {
              this.setState(prevState => ({
                ...prevState,
                loadingByUniqueId: false
              }));
            });
        } catch (error) {
          this.setState(prevState => ({
            ...prevState,
            loadingByUniqueId: false
          }));
          throw error;
        }
      }
    } else if (
      uniqueId &&
      !this.state.partner.document.generalData.nationalityId &&
      !this.state.partner.document.generalData.isExternal &&
      isCNP(uniqueId)
    ) {
      this.props.actions.getNationalityByUniqueId(uniqueId, !!this.state.partner.document.generalData.isExternal).then(nationalityId => {
        this.setState(prevState => ({
          ...prevState,
          partner: {
            ...prevState.partner,
            document: {
              ...prevState.partner.document,
              generalData: {
                ...prevState.partner.document.generalData,
                nationalityId: nationalityId,
                citizenshipId: nationalityId
              }
            }
          }
        }));
      });
    }
  };

  handlePropertyChange = curry((prop, value) => {
    const addressIndex = parseInt(prop.split('.')[0]);
    const propName = prop.split('.')[1];
    const { generalData, partnerType, contactMode } = this.state.partner.document;
    const { isAssociatedPartner, partnerTypes, idTypes } = this.props;
    const selectedIdTypeCode = idTypes
      ? idTypes.find(x => x.id === this.state.partner.document.generalData.personDocumentTypeId)?.code
      : '';
    const minAge = generalData.minimumAge || minimumAge;
    const maxAge = generalData.maximumAge || maximumAge;
    const uniqueIdErrorMessage = this.props.t('Validations.Client.Age', {
      data: { minimumAge: minAge, maximumAge: maxAge }
    });
    const uniqueId = prop.includes('uniqueId') ? value : null;
    const { validityDaysForIdentityCard } = this.props.params;
    let hasMultipleEmailAddresses = Boolean(contactMode?.email?.split(';').length > 1);
    switch (propName) {
      case 'partnerName':
      case 'uniqueId': {
        this.setState(prevState => ({
          ...prevState,
          canSearchExternally: {
            ...prevState.canSearchExternally,
            [`${propName}IsCIF`]: validateCIF(value),
            [`${propName}IsValid`]: this.checkMandatoryFields() ? !!value : true
          }
        }));
        break;
      }
      case 'isExternal': {
        this.setState(prevState => ({
          ...prevState,
          partner: {
            ...prevState.partner,
            document: {
              ...prevState.partner.document,
              generalData: {
                ...prevState.partner.document.generalData,
                isExternal: value
              }
            },
            dirtyInfo: {
              ...prevState.partner.dirtyInfo,
              isDirty: true
            }
          }
        }));
        break;
      }
      case 'country': {
        this.setState(prevState => ({
          partner: flow(
            editableDocument.update('addresses.' + addressIndex + '.country', value, null),
            editableDocument.update('addresses.' + addressIndex + '.county', null, null),
            editableDocument.update('addresses.' + addressIndex + '.city', null, null)
          )(prevState.partner)
        }));
        if (value) {
          this.props.actions.loadCounties('', value.id);
        }
        break;
      }
      case 'county': {
        // TODO: fix cyclical business rules.
        this.setState(prevState => ({
          partner: flow(
            editableDocument.update('addresses.' + addressIndex + '.county', value, null),
            editableDocument.update('addresses.' + addressIndex + '.city', null, null)
          )(prevState.partner)
        }));
        break;
      }
      case 'city': {
        // TODO: fix cyclical business rules.
        this.setState(prevState => ({
          partner: editableDocument.update('addresses.' + addressIndex + '.city', value, null)(prevState.partner)
        }));
        break;
      }
      case 'street':
      case 'number':
      case 'building':
      case 'stair':
      case 'flatNumber':
      case 'floor':
      case 'neighborhood':
      case 'isActive':
      case 'isDefault':
      case 'zipCode': {
        // TODO: fix cyclical business rules.
        this.setState(prevState => ({
          partner: editableDocument.update('addresses.' + addressIndex + '.' + propName, value, null)(prevState.partner)
        }));
        break;
      }
      case 'email': {
        hasMultipleEmailAddresses = Boolean(value?.split(';').length > 1);
        break;
      }
      default:
        break;
    }

    this.setState(
      prevState => ({
        partner: flow(
          prop !== 'partnerRoleId' ? editableDocument.update(prop, value, this.getBusinessRules()) : x => x,
          editableDocument.validate(
            getValidationRules(
              prop === 'partnerType' ? value?.partnerTypeId : partnerType?.partnerTypeId,
              partnerTypes,
              minAge,
              maxAge,
              uniqueIdErrorMessage,
              prop === 'generalData.identityCardExpireDate' ? value : generalData.identityCardExpireDate,
              prop === 'generalData.identityCardReleaseDate' ? value : generalData.identityCardReleaseDate,
              isAssociatedPartner,
              this.checkMandatoryFields(),
              this.applyUniqueIdRules(uniqueId, prevState.partner?.document?.generalData?.isExternal),
              validityDaysForIdentityCard,
              generalData.uniqueId,
              hasMultipleEmailAddresses,
              this.props.params.countryCode,
              selectedIdTypeCode === 'PAS',
              prevState.partner?.document?.generalData?.isExternal,
              this.applyBirthDateRule(generalData.uniqueId),
              this.props.offerPartnerExtendedInformationRead
            ),
            true
          )
        )(prevState.partner)
      }),
      () => {
        const currentValue = get(this.state.partner.document, prop);
        switch (prop) {
          case 'generalData.uniqueId': {
            if (this.state.partnerFromModal) {
              this.handleUniqueIdBlur(value);
              this.setIsPartnerFromModal(false);
            }
            break;
          }
          case 'partnerType': {
            this.props.actions.loadDefaultPartnerAddress(currentValue?.partnerTypeId);
            this.setState(prevState => ({
              partner: flow(editableDocument.resetDirtyInfo, editableDocument.resetValidationInfo)(prevState.partner)
            }));

            this.setState(prevState => ({
              ...prevState,
              partner: {
                ...prevState.partner,
                document: {
                  ...prevState.partner.document,
                  generalData: {},
                  contactMode: {},
                  addresses: [this.props.defaultAddress[currentValue?.partnerTypeId]]
                }
              }
            }));
            break;
          }

          case 'agreesWithPersonalDataUse': {
            this.setState(prevState => ({
              partner: flow(
                editableDocument.update('generalData', {}, null),
                editableDocument.update('contactMode', {}, null),
                editableDocument.update('addresses', [this.props.defaultAddress[this.props.partner.partnerType?.partnerTypeId]], null),
                editableDocument.resetDirtyInfo,
                editableDocument.resetValidationInfo
              )(prevState.partner)
            }));
            break;
          }

          case 'partnerRoleId': {
            this.setState(prevState => ({
              partner: flow(
                editableDocument.update('partnerRoleId', value.partnerRoleId, null),
                editableDocument.update('partnerRoleCode', value.partnerRoleCode, null),
                editableDocument.update('partnerRoleName', value.name, null)
              )(prevState.partner)
            }));

            break;
          }

          case 'generalData.isExternal': {
            this.setState(prevState => ({
              partner: flow(
                editableDocument.update('generalData.isExternal', value, null),
                editableDocument.resetValidationInfo
              )(prevState.partner)
            }));
            break;
          }

          default:
            break;
        }
      }
    );
  });

  handleDelete = () => {
    const { documentId, siteId, partnerId } = this.props;
    this.props.onDelete({ documentId, siteId, partnerId });
  };

  handleDeleteDocumentPartner = () => {
    const { documentId, siteId, partnerId } = this.props;
    this.props.onDeleteDocumentPartner({ documentId, siteId, partnerId });
  };

  handleCardToggle = () => {
    const { actions, isAssociatedPartner, isRealBeneficiary, partnerId } = this.props;
    actions.togglePartnerFormCard(partnerId, isAssociatedPartner, isRealBeneficiary);
  };

  render() {
    const {
      partnerId,
      isLoaded,
      loading,
      isAssociatedPartner,
      isRealBeneficiary,
      saving,
      deleting,
      expanded,
      printingGdpr,
      documentId,
      siteId,
      params,
      dictionaryAddressType,
      offerTypeId,
      isFacilityFrame,
      partnerAddresses,
      realBeneficiaryRole,
      partnerFileData
    } = this.props;
    const { document, validationInfo, dirtyInfo } = this.state.partner;

    return (
      <OfferPartnerFormCard
        partnerId={partnerId}
        partner={document}
        documentId={documentId}
        siteId={siteId}
        isLoaded={isLoaded}
        loading={loading}
        isAssociatedPartner={isAssociatedPartner}
        isRealBeneficiary={isRealBeneficiary}
        realBeneficiaryRole={realBeneficiaryRole}
        validationInfo={validationInfo}
        checkMandatoryFields={this.checkMandatoryFields()}
        isDirty={dirtyInfo.isDirty && !this.state.isSaving}
        onPropertyChange={this.handlePropertyChange}
        onUniqueIdBlur={this.handleUniqueIdBlur}
        onSave={this.handleSave}
        expanded={expanded}
        onCardToggle={this.handleCardToggle}
        saving={saving || partnerFileData.saving}
        deleting={deleting}
        onDelete={this.handleDelete}
        onDeleteDocumentPartner={this.handleDeleteDocumentPartner}
        printingGdpr={printingGdpr}
        onGDPRPrint={this.onGDPRPrint}
        addressTypes={params.partnerAddressTypes}
        socialAddressTypeId={params.socialAddressTypeId}
        onAddAddress={this.onAddAddress}
        setIsPartnerFromModal={this.setIsPartnerFromModal}
        onDeleteAddress={this.onDeleteAddress}
        defaultPartnerType={this.props.offerConfig.defaultPartnerType}
        dictionaryAddressType={dictionaryAddressType}
        partnerAddresses={partnerAddresses}
        offerTypeId={offerTypeId}
        isFacilityFrame={isFacilityFrame}
        canSearchExternally={this.state.canSearchExternally}
        handleExternalSearch={this.handleExternalSearch}
        waitingForOCR={this.props.waitingForOCR}
        loadingByUniqueId={this.state.loadingByUniqueId}
      />
    );
  }
}

OfferPartnerFormContainer.propTypes = {
  actions: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  partner: PropTypes.object.isRequired,
  partnerByUniqueId: PropTypes.object.isRequired,
  isLoadedByUniqueId: PropTypes.string,
  partnerFromQuery: PropTypes.object,
  isLoaded: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  isAssociatedPartner: PropTypes.bool,
  isRealBeneficiary: PropTypes.bool,
  saving: PropTypes.bool.isRequired,
  deleting: PropTypes.bool.isRequired,
  expanded: PropTypes.bool.isRequired,
  printingGdpr: PropTypes.bool.isRequired,
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  partnerId: PropTypes.number.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDeleteDocumentPartner: PropTypes.func.isRequired,
  offerConfig: PropTypes.object.isRequired,
  dictionaryAddressType: PropTypes.array.isRequired,
  offerTypeId: PropTypes.number,
  documentTypeId: PropTypes.number.isRequired,
  isFacilityFrame: PropTypes.bool,
  waitingForOCR: PropTypes.bool.isRequired,
  isExternalVerificationInProgress: PropTypes.bool,
  partnerIsFromPartnerQuery: PropTypes.bool,
  dictionaryAddressTypeLoaded: PropTypes.bool,
  partnerAddressesLoaded: PropTypes.bool,
  defaultAddress: PropTypes.object,
  countiesLoaded: PropTypes.bool,
  countiesLoading: PropTypes.bool,
  params: PropTypes.object,
  genderTypes: PropTypes.array,
  partnerTypes: PropTypes.array,
  partnerAddresses: PropTypes.array,
  idTypes: PropTypes.array,
  realBeneficiaryRole: PropTypes.object,
  partnerFileData: PropTypes.object,
  history: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  shouldRefreshAddresses: PropTypes.bool.isRequired,
  offerPartnerExtendedInformationRead: PropTypes.bool
};

function mapStateToProps(state, ownProps) {
  let countryId;

  const defaultPartnerAddress = ownProps.partner?.addresses?.find(a => a.isDefault);
  if (!defaultPartnerAddress) countryId = state.offerConfig.defaultCountry.id;
  else countryId = defaultPartnerAddress.country.id;

  const { documentTypeId, stateId } = state.currentOffer.header;
  const rightsStateKey = getDocumentRightsByStateKey(stateId, documentTypeId);
  const rights = state.documentRights[rightsStateKey] || {};
  const { offerPartnerExtendedInformationRead } = rights;

  const partnerId = ownProps.partnerId;
  const partnerAddresses = getPartnerAddresses(state, partnerId);
  const shouldRefreshAddresses = getShouldRefreshAddresses(state, partnerId);
  const partnerAddressesLoaded = state.currentOffer.clientAddressesData[partnerId]?.loaded;
  const { printingGdpr, saving, deleting } = getClientData(state, partnerId);
  const partnerFileData = getPartnerFileData(state);
  const partner = getFullPartner(state, partnerId);
  const partnerByUniqueId = getPartnerByUniqueId(state, partnerId);
  const isLoadedByUniqueId = getPartnerIsLoadedByUniqueId(state, partnerId);
  const partnerFromQuery = getPartnerFromQuery(state, partnerByUniqueId.generalData?.uniqueId || partner.generalData?.uniqueId);
  const isLoaded = getPartnerIsLoaded(state, partnerId);
  const loading = getPartnerLoading(state, partnerId);
  const expanded = getPartnerExpanded(state, partnerId);
  const addressTypes = getAddressTypes(state);
  const addressTypesLoaded = getAddressTypesLoaded(state);
  const genderTypes = state.dictionaries.genderTypes.isLoaded ? state.dictionaries.genderTypes.items : [];
  const defaultAddress = getDefaultAddressData(state);
  const offerTypeId = getOfferType(state);
  const partnerTypes = getFilteredPartnerTypes(state, ownProps.isFacilityFrame);
  const countiesLoaded = getCountiesLoadedByCountryId(state, countryId);
  const countiesLoading = getCountiesLoadingByCountryId(state, countryId);
  const waitingForOCR = state.currentOffer.clientData.waitingForOCR;
  const isExternalVerificationInProgress = getExternalVerificationStatusByUniqueId(
    state,
    partner.generalData?.uniqueId,
    ownProps.partnerId
  );
  const partnerIsFromPartnerQuery = getRefreshedOfferPartnerStatus(state, partner.generalData?.uniqueId, ownProps.partnerId);
  const idTypes = state.clientData.idTypes;

  const realBeneficiaryRole = getRealBeneficiaryRole(state.offerConfig, partnerId);

  return {
    partner,
    partnerByUniqueId,
    partnerFromQuery,
    isLoadedByUniqueId,
    printingGdpr,
    isLoaded,
    loading,
    saving,
    deleting,
    expanded,
    offerConfig: state.offerConfig,
    dictionaryAddressType: addressTypes,
    dictionaryAddressTypeLoaded: addressTypesLoaded,
    genderTypes,
    defaultAddress,
    offerTypeId,
    partnerTypes,
    countiesLoaded,
    countiesLoading,
    waitingForOCR,
    isExternalVerificationInProgress,
    partnerIsFromPartnerQuery,
    partnerAddresses,
    partnerAddressesLoaded,
    idTypes,
    realBeneficiaryRole,
    partnerFileData,
    shouldRefreshAddresses,
    offerPartnerExtendedInformationRead
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadDocumentPartner,
        loadDefaultPartnerAddress,
        setDefaultPartnerAddress,
        cancelOfferClientChanges,
        togglePartnerFormCard,
        loadCounties,
        loadPartnerByUniqueId,
        getNationalityByUniqueId,
        loadAddressTypes,
        saveOfferPartner,
        onPrintGDPR,
        setBirthDateFromCNP,
        finishPartnerQuery,
        loadPartnerAdresses,
        createPartnerFile,
        refreshAddressesFinished
      },
      dispatch
    ),
    dispatch
  };
}

export default withRouter(compose(connect(mapStateToProps, mapDispatchToProps), withTranslation())(withParams(OfferPartnerFormContainer)));
